import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Card, CardText } from 'react-toolbox/lib/card';
import {Button} from '../../../../common/components';
import ButtonLink from '../../../../common/components/ButtonLink';
import HorizontalRule from '../HorizontalRule';
import {
  TitleBar,
  DocTitle,
} from '../../../../common/components/Layout/StandardPageTemplate/Context';
import { getCurrentUser } from '../../../../redux-modules/current-user';
import { withLoading } from '../../../../redux-modules/loading-overlay';
import PreferencesActions from '../../actions/customers/preferences';
import { getPreferences } from '../../selectors/customers/preferences';
import ActionHandler from '../ActionHandler';
import SwitchList from '../SwitchList';
import styles from './PrivacyPage.css';

class PrivacyPage extends React.Component {
  static propTypes = {
    isLoading: PropTypes.bool,
    currentUser: PropTypes.shape({
      id: PropTypes.string,
    }),
    preferences: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        displayText: PropTypes.string,
        category: PropTypes.oneOf([
          'None',
          'CommunicationPreferences',
          'MemberPrivacy',
          'GetInvolved',
          'TopicsOfConcern',
        ]),
        isSelected: PropTypes.bool,
      })
    ),
    actions: PropTypes.shape({
      preferences: PropTypes.objectOf(PropTypes.func),
    }),
  };
  static showInDirectoryKey = '6c94984e-c169-4aa5-a27d-54c812e6b4d3';
  static page = {
    color: 'red',
    title: 'Privacy Settings',
  };
  static prefsToNegate = [
    '7f2ed28a-d491-4e83-b445-1dfe6f4dc77a',
    'fd6b295b-ecea-4993-98c5-524ad2a559ac',
  ];
  static promises = {
    includePromises: [
      { type: 'ENTITY/ENTITY_GET', meta: { namespace: 'preferences' } },
      { type: 'ENTITY/ENTITY_PUT', meta: { namespace: 'preferences' } },
    ],
  };

  constructor(props) {
    super(props);
    this.state = {
      form: this.loadPreferencesFromProps(props),
      save: {
        shouldSave: false,
        data: [],
      },
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isLoading) {
      return;
    }
    this.setState(prevState => {
      return {
        ...prevState,
        form: {
          ...prevState.form,
          ...this.loadPreferencesFromProps(nextProps),
        },
      };
    });
  }

  setFormValue = formProps => {
    this.setState(prevState => {
      return {
        ...prevState,
        form: {
          ...prevState.form,
          ...formProps,
        },
      };
    });
  };

  loadPreferencesFromProps = props => {
    const { preferences } = props;
    return {
      ...preferences.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.key]: curr.isSelected,
        };
      }, {}),
    };
  };

  handleChecked = key => value => {
    this.setFormValue({ [key]: value });
  };

  updateSave = ({ shouldSave, data = [] }) => {
    this.setState(prevState => ({
      ...prevState,
      save: {
        shouldSave,
        data,
      },
    }));
  };

  saveAction = data => {
    this.updateSave({ shouldSave: false });
    return this.props.actions.preferences.put(data);
  };

  handleSubmit = () => {
    const { ...preferences } = this.state.form;
    const data = {
      items: this.props.preferences.map(i => {
        let isSelected = preferences[i.key];
        if (PrivacyPage.prefsToNegate.indexOf(i.key) >= 0) {
          isSelected = !isSelected;
        }
        return {
          ...i,
          isSelected,
        };
      }),
    };
    this.updateSave({ shouldSave: true, data });
  };

  isDisabled = item => {
    const isDirectoryEnabled = !!this.state.form[
      PrivacyPage.showInDirectoryKey
    ];
    return !(item.key === PrivacyPage.showInDirectoryKey || isDirectoryEnabled);
  };

  render() {
    const { preferences } = this.props;
    const filteredPreferences = preferences.filter(
      i => i.category === 'MemberPrivacy' && i.key
    );
    return (
      <div>
        <TitleBar title={PrivacyPage.page.title} />
        <DocTitle title={PrivacyPage.page.title} />
        <h4 className={styles.header}>I would like to</h4>
        <Card className={styles.card}>
          <CardText style={{ textTransform: 'capitalize' }}>
            <ActionHandler
              condition={
                !!this.props.currentUser.id &&
                (!preferences || !preferences.length)
              }
              action={this.props.actions.preferences.get}
            />
            <ActionHandler
              condition={this.state.save.shouldSave}
              action={this.saveAction}
              data={[this.state.save.data]}
            />
            <SwitchList
              items={filteredPreferences}
              data={this.state.form}
              onChange={this.handleChecked}
              color={PrivacyPage.page.color}
              disabled={this.isDisabled}
              sortKey="order"
            >
              <h4 style={{ textAlign: 'center' }}>Loading...</h4>
            </SwitchList>
          </CardText>
        </Card>
        <Button
          primary
          raised
          onClick={this.handleSubmit}
          className={styles.button}
          style={{ marginTop: 8 }}
          label="Save"
        />
        <HorizontalRule color={PrivacyPage.page.color} />
        <ButtonLink
          primary
          raised
          to="/account"
          className={styles.button}
          label="Back to Account Management"
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const currentUser = getCurrentUser(state);
  const preferences = getPreferences(state).map(pref => {
    let displayText = (pref.displayText || '').toLowerCase();
    if (PrivacyPage.prefsToNegate.indexOf(pref.key) >= 0) {
      if (displayText.slice(0, 4) === 'not ') {
        displayText = displayText.slice(4, displayText.length + 1);
      }
      return {
        ...pref,
        isSelected: !pref.isSelected,
        displayText,
      };
    }
    return {
      ...pref,
      displayText,
    };
  });

  return {
    currentUser,
    ...(currentUser.settings || {}).contactPreferences,
    preferences,
  };
};

// return dispatch to be used in mergeProps where binding will take place
const mapDispatchToProps = dispatch => {
  return { dispatch };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return {
    ...ownProps,
    ...stateProps,
    actions: {
      preferences: bindActionCreators(
        PreferencesActions(stateProps.currentUser.id),
        dispatchProps.dispatch
      ),
    },
  };
};

export default withLoading(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(PrivacyPage)
);



// WEBPACK FOOTER //
// ./src/App/account/components/PrivacyPage/index.js